import { useState, useRef, useEffect } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import MobileBottomNav from '../common/MobileBottomNav'
import NotificationBell from '../common/NotificationBell'
import NotificationPermission from '../common/NotificationPermission'
import InstallPWA from '../common/InstallPWA'
import { Home, FileText, Clock, CalendarDays, User, LogOut, ChevronDown, BookOpen } from 'lucide-react'

interface NavItem {
  name: string
  href: string
  icon: React.ElementType
  module?: string
}

const desktopNavItems: NavItem[] = [
  { name: 'Home', href: '/dashboard', icon: Home, module: 'pwa_dashboard' },
  { name: 'KPI', href: '/kpi', icon: FileText, module: 'pwa_kpi_hub' },
  { name: 'Attendance', href: '/attendance', icon: Clock, module: 'pwa_attendance' },
  { name: 'Leave', href: '/leave', icon: CalendarDays, module: 'pwa_leave' },
  { name: 'Learning', href: '/learning', icon: BookOpen, module: 'pwa_learning' },
]

export default function MobileLayout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const { canAccessPage } = usePermissions()
  const [profileOpen, setProfileOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const filteredNav = desktopNavItems.filter(item => !item.module || canAccessPage(item.module))

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 safe-area-inset-top">
        <div className="flex items-center justify-between h-14 px-4">
          <h1 className="text-lg font-bold text-primary-600">{user?.company?.name || 'EVO'}</h1>

          {/* Desktop nav links - hidden on mobile (bottom nav handles it) */}
          <nav className="hidden lg:flex items-center gap-1">
            {filteredNav.map(item => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <NotificationBell />

            {/* Mobile: avatar only */}
            <div className="lg:hidden h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-600 text-sm font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>

            {/* Desktop: profile dropdown */}
            <div className="hidden lg:block relative" ref={dropdownRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 text-sm font-medium">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900 leading-tight">{user?.name}</p>
                  <p className="text-xs text-gray-500 capitalize leading-tight">
                    {user?.ev_designation_name || user?.role?.replace('_', ' ')}
                  </p>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <NavLink
                    to="/profile"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <User className="h-4 w-4" />
                    Profile
                  </NavLink>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Notification permission prompt */}
      <NotificationPermission />

      {/* Page content */}
      <main className="pb-20 lg:pb-6">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation (hidden on lg+ via CSS) */}
      <MobileBottomNav />
      <InstallPWA />
    </div>
  )
}
